import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    id: "pro",
    price: "$99",
    name: "Fix Manual",
    color: "border-openai-cyan/40",
    features: ["Full Fix Manual (step-by-step)", "Competitor Deep-Dive Share-of-Voice", "Metadata Audit tool - scan your website's code"],
  },
  {
    id: "truth",
    price: "$299",
    name: "Truth Verifier",
    color: "border-truth-green/40",
    features: ["Everything in Fix Manual", "Verify with Truth - URL/PDF audit vs AI claims", "Verification seal & discrepancy report"],
  },
  {
    id: "agency",
    price: "$799",
    name: "Sovereign Agency",
    color: "border-sovereign/60",
    features: ["Everything in Truth Verifier", "PDF Reputation Scorecard", "Weekly Automated Audit + Signal Change alerts", "1:1 Sovereign Strategy Consultation"],
  },
  {
    id: "sovereign_defense",
    price: "$5,000/mo",
    name: "Sovereign Defense",
    color: "border-warning-amber/60",
    features: ["Everything in Sovereign Agency", "Predictive Anomaly Alerts (Unreported Risks)", "Executive Counter-Signal Direct Orders", "Competitor Hijack Alerts", "Optional Hourly Signal Probes", "Track up to 5 Named Competitors"],
  },
];

export const PaywallTiers = ({ auditId, currentTier = "free", spotsRemaining }) => {
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = async (packageId) => {
    if (!user) {
      navigate(`/login?next=/audit/${auditId}`);
      return;
    }
    setLoadingId(packageId);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        audit_id: auditId,
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  const RANK = { free: 0, pro: 1, truth: 2, agency: 3, sovereign_defense: 4 };

  return (
    <div data-testid="paywall-tiers">
      {!user && (
        <p className="mb-4 text-center text-xs text-slate-500" data-testid="paywall-login-notice">
          You'll need to log in or sign up to purchase and unlock ownership of this audit.
        </p>
      )}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {TIERS.map((tier) => {
          const owned = RANK[currentTier] >= RANK[tier.id];
          return (
            <div key={tier.id} className={`flex flex-col rounded-lg border ${tier.color} bg-surface-card p-6`} data-testid={`tier-card-${tier.id}`}>
              <h3 className="font-heading text-lg font-bold text-white">{tier.name}</h3>
              <p className="mb-4 font-heading text-3xl font-extrabold text-white">{tier.price}</p>
              {tier.id === "agency" && (
                <>
                  <span className="mb-3 inline-block rounded-full border border-sovereign/50 bg-sovereign/10 px-3 py-1 text-xs font-semibold text-sovereign" data-testid="scarcity-badge">
                    Only {spotsRemaining ?? 5} Sovereign spots remaining
                  </span>
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-warning-amber/30 bg-warning-amber/5 p-3" data-testid="directors-bonus-card">
                    <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-amber" />
                    <p className="text-xs text-warning-amber">
                      <strong>Director's Bonus:</strong> Successful purchase unlocks 1 of 5 monthly Sovereign Strategy Sessions with the Founder.
                    </p>
                  </div>
                </>
              )}
              <ul className="mb-6 flex-1 space-y-2">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-truth-green" /> {f}
                  </li>
                ))}
              </ul>
              {owned ? (
                <Button disabled className="w-full bg-truth-green/20 text-truth-green" data-testid={`tier-owned-${tier.id}`}>
                  <Check className="mr-2 h-4 w-4" /> Unlocked
                </Button>
              ) : (
                <Button
                  onClick={() => checkout(tier.id)}
                  disabled={loadingId === tier.id}
                  className="w-full gap-2 whitespace-normal bg-white px-3 text-sm text-obsidian hover:bg-slate-200"
                  data-testid={`checkout-tier-${tier.id}-btn`}
                >
                  <Lock className="h-4 w-4 flex-shrink-0" /> {loadingId === tier.id ? "Redirecting..." : `Unlock ${tier.price}`}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
